import { useState } from 'react';
import { StyledContainer, Select } from '@piwikpro/ui-components';
import { HOUR } from '../helpers';
import { useAppSelector } from '../hooks';
import {
  selectComponentDowntime,
  selectComponentsMapping,
} from '../incidentSlice';
import { UptimeGraphListItem } from './UptimeGraphListItem';

import './UptimeGraphList.css';

const TIME_RANGES = [
  'Last 24h',
  'Today',
  'Yesterday',
  'Last 7 days',
  'Last week',
  'Last 30 days',
  'Last month',
  'Last year',
  'This year',
];

const UptimeGraphList: React.FC = () => {
  const [selectedRange, setSelectedRange] = useState('Last 24h');
  const downtimes = useAppSelector(selectComponentDowntime);
  const componentsMapping = useAppSelector(selectComponentsMapping);

  const components = downtimes.reduce(
    (acc: string[], v) => {
      if (!acc.includes(v.name)) {
        acc.push(v.name);
      }
      return acc;
    },
    componentsMapping.map((c) => c.displayName)
  );

  const mockDate = new URLSearchParams(document.location.search).get(
    'mockDate'
  );

  const end = mockDate ? new Date(mockDate) : new Date();
  const start = new Date(end);

  switch (selectedRange) {
    case 'Last 24h':
      start.setTime(end.getTime() - 24 * HOUR);
      break;
    case 'Today':
      start.setHours(0, 0, 0, 0);
      break;
    case 'Yesterday':
      start.setDate(start.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      end.setDate(end.getDate() - 1);
      end.setHours(23, 59, 59, 999);
      break;
    case 'Last 7 days':
      start.setDate(start.getDate() - 7);
      break;
    case 'Last week':
      const dayOfWeek = start.getDay(); // Sunday = 0, Monday = 1, ...
      const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert Sunday (0) to last Monday (-6)
      start.setDate(start.getDate() - daysSinceMonday - 7);
      start.setHours(0, 0, 0, 0);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      break;
    case 'Last 30 days':
      start.setDate(start.getDate() - 30);
      start.setHours(0, 0, 0, 0);
      break;
    case 'Last month':
      start.setMonth(start.getMonth() - 1);
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end.setDate(0);
      end.setHours(23, 59, 59, 999);
      break;
    case 'Last year':
      start.setFullYear(start.getFullYear() - 1);
      start.setMonth(0, 1);
      start.setHours(0, 0, 0, 0);
      end.setFullYear(end.getFullYear() - 1);
      end.setMonth(11, 31);
      end.setHours(23, 59, 59, 999);
      break;
    case 'This year':
      start.setFullYear(start.getFullYear());
      start.setMonth(0, 1);
      start.setHours(0, 0, 0, 0);
      break;
    default:
      break;
  }

  const handleSelectChange = (event: { target: { value: string } }) => {
    setSelectedRange(event.target.value);
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <h2 style={{ margin: 0 }}>System Components</h2>
        <div style={{ width: '200px' }}>
          <Select
            options={[
              {
                id: 'timeRange',
                name: 'Time Range',
                items: TIME_RANGES.map((value, index) => ({
                  id: index,
                  name: value,
                  value: value,
                })),
              },
            ]}
            value={selectedRange}
            onChange={handleSelectChange}
            placeholder="Select Time Range"
            dataId="timeRangeSelect"
            name={''}
          />
        </div>
      </div>
      {components.map((c) => (
        <StyledContainer
          border={['bottom', 'left', 'right', 'top']}
          padding="wide"
          key={c}
        >
          <UptimeGraphListItem
            key={c}
            start={start}
            end={end}
            downtimes={downtimes}
            component={c}
            selectedRangeLabel={selectedRange}
          />
        </StyledContainer>
      ))}
    </>
  );
};

export { UptimeGraphList };
